import { USER_ROLES } from '../utils/constants';
import { authDebugEnabled, logAuthDebug } from './roleHeaders';

/**
 * Relative tutor REST paths (axios `config.url`) that the ai-tutor service guards with
 * `require_ai_tutor_student` (or equivalent student-only semantics).
 * Paths including `/teacher/` are excluded — those are teacher-scoped.
 */
export function requiresAiTutorStudentScope(relUrl: string | undefined): boolean {
  if (!relUrl) return false;
  const isTutorMount =
    relUrl.includes('/v1/tutor/') ||
    relUrl.includes('/tutor/') ||
    /** Some tests use bare `tutor` segment */
    /(^|\/)tutor\/sessions(\/|$)/.test(relUrl);
  if (!isTutorMount) return false;

  if (relUrl.includes('/teacher/')) return false;
  if (relUrl.includes('/analytics/teacher')) return false;
  if (relUrl.includes('/progress/analytics/class')) return false;

  if (relUrl.includes('/session/start')) return true;
  if (relUrl.includes('/session/ping')) return true;
  if (relUrl.includes('/session/summary')) return true;
  if (relUrl.includes('/analytics/me/')) return true;
  if (relUrl.includes('/system-instruction')) return true;
  if (relUrl.includes('/session-token')) return true;

  if (relUrl.includes('/students/me')) return true;
  if (relUrl.includes('/progress/analytics/student')) return true;
  if (relUrl.includes('/lesson-index')) return !relUrl.includes('teacher/lesson-index');
  if (relUrl.includes('/progress/lesson/')) return true;
  if (relUrl.includes('/progress/batch')) return true;
  if (relUrl.includes('/progress/update')) return true;
  if (relUrl.includes('/session/update')) return true;
  if (relUrl.includes('/resume/')) return true;
  if (relUrl.includes('/sessions')) return true;
  return false;
}

/** Active shell role (after `applyRoleHeadersFromUser`) must be STUDENT for student-only tutor REST. */
export function localStorageActiveRoleAllowsStudentTutorApis(): boolean {
  try {
    return localStorage.getItem('X_ROLE') === USER_ROLES.STUDENT;
  } catch {
    return false;
  }
}

export function assertStudentTutorRestOrThrow(relUrl: string | undefined, method?: string): void {
  if (!requiresAiTutorStudentScope(relUrl)) return;
  if (localStorageActiveRoleAllowsStudentTutorApis()) return;
  const role = (() => {
    try {
      return localStorage.getItem('X_ROLE');
    } catch {
      return null;
    }
  })();
  const msg = `[auth] Blocked ${method ?? 'REQ'} ${relUrl ?? ''}: X_ROLE="${role}" cannot call student-scoped AI Tutor APIs (pick Student on login or use a student account).`;
  if (authDebugEnabled()) logAuthDebug('tutor_student_scope_blocked', { relUrl, method, role });
  throw new Error(msg);
}
